import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Card from "@mui/material/Card";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import HelpTooltip from "components/HelpTooltip";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import DefaultInfoCard from "components/patterns/DefaultInfoCard";
import DataTable from "components/patterns/DataTable";
import WTPChart from "./components/WTPChart";
import ReportsBarChart from "components/patterns/charts/ReportsBarChart";
import MultiAxisChart from "components/patterns/charts/MultiAxisChart";
import Footer from "components/layout/Footer";

import { useApiClient } from "common/ApiClientProvider";

function EnvironmentalValue() {
  const { t, i18n } = useTranslation("pages");
  const { t: tHelp } = useTranslation("help");
  const location = useLocation();
  const apiClient = useApiClient();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedSiteBody, setSelectedSiteBody] = useState({});
  const [impactAndCumulativePowerGraphData, setImpactAndCumulativePowerGraphData] = useState({});
  const [impactGraphData, setImpactGraphData] = useState({});
  const [lcoeData, setLcoeData] = useState({});
  const [remainingServiceLife, setRemainingServiceLife] = useState(0);
  const [remainingPower, setRemainingPower] = useState(0);
  const [currentWTP, setCurrentWTP] = useState(0);
  const [tableData, setTableData] = useState({
    columns: [
      { Header: t("environmentalUi.years"), accessor: "years" },
      { Header: t("environmentalUi.cumulativePower"), accessor: "cumulativePower" },
      {
        Header: t("environmentalUi.impactPerUnit"),
        accessor: "environmentalImpact",
      },
      { Header: t("environmentalUi.totalImpact"), accessor: "total" },
    ],
    rows: [],
  });
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);

  async function fetchSiteData(siteId) {
    const response = await apiClient.get("get_trend_result", {
      query: { system_type: "ongrid" },
    });
    const displaySite = response.sites.filter((site) => site.site == siteId)[0];
    setRemainingServiceLife(displaySite.service_life_rates.length);
  }
  async function fetchSiteImpact(siteId) {
    const response = await apiClient.get("environmentalvalues/", {
      query: { site_id: siteId },
    });
    const serviceLifeYears = response.service_life_years;
    setImpactAndCumulativePowerGraphData({
      labels: ["10", "15", "30", serviceLifeYears.toString()],
      units: { x: t("environmentalUi.years"), y: "mPt/kWh", y2: "kWh" },
      datasets1: [
        {
          label: t("environmentalUi.impactPerUnit"),
          data: response.environmental_impacts,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
        {
          label: t("environmentalValue.junctionBoxRepair"),
          data: response.junctionbox_repair,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
        {
          label: t("environmentalValue.transport100km"),
          data: response.transport_100km,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
        },
        {
          label: t("environmentalValue.transport10000km"),
          data: response.transport_10000km,
          backgroundColor: "rgba(255, 255, 0, 0.8)",
        },
      ],
      datasets2: [
        {
          label: t("environmentalUi.cumulativePower"),
          data: response.cumulative_power,
          backgroundColor: "rgba(75, 255, 255, 0.8)",
        },
      ],
    });
    setImpactGraphData({
      labels: ["10", "15", "30"],
      units: { x: t("environmentalUi.years"), y: "mPt/kWh" },
      datasets: [
        {
          label: t("environmentalValue.impactScenario1"),
          data: response.environmental_impacts,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          stack: "Stack 0",
        },
        {
          label: t("environmentalValue.impactScenario2"),
          data: response.environmental_impacts_2,
          backgroundColor: "rgba(0, 255, 255, 0.8)",
          stack: "Stack 1",
        },
        {
          label: t("environmentalValue.impactScenario3"),
          data: response.environmental_impacts_3,
          backgroundColor: "rgba(255, 0, 255, 0.8)",
          stack: "Stack 2",
        },
        {
          label: t("environmentalValue.impactScenario4"),
          data: response.environmental_impacts_2,
          backgroundColor: "rgba(255, 255, 0, 0.8)",
          stack: "Stack 3",
        },
      ],
    });
    setRemainingPower(response.site_a_wtp[0]);
    setCurrentWTP(response.site_a_wtp[1]);
    setLcoeData({
      labels: [
        "40%",
        "45%",
        "50%",
        "55%",
        "60%",
        "65%",
        "70%",
        "75%",
        "80%",
        "85%",
        "90%",
        "95%",
        "100%",
      ],
      datasets: [
        {
          label: t("environmentalValue.remainingServiceLife", { years: 30 }),
          data: response.lcoe[0],
          backgroundColor: "rgba(255, 255, 0, 0.8)",
        },
        {
          label: t("environmentalValue.remainingServiceLife", { years: 20 }),
          data: response.lcoe[1],
          backgroundColor: "rgba(255, 0, 255, 0.8)",
        },
        {
          label: t("environmentalValue.remainingServiceLife", { years: 15 }),
          data: response.lcoe[2],
          backgroundColor: "rgba(0, 255, 255, 0.8)",
        },
        {
          label: t("environmentalValue.remainingServiceLife", { years: 10 }),
          data: response.lcoe[3],
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        },
      ],
      /*
      datasets2: [
        {
          label: "site WTP",
          data: [
            {
              x: response.site_a_wtp[0],
              y: response.site_a_wtp[1],
            },
          ],
        },
      ],
      */
    });
    setTableData({
      columns: [
        { Header: t("environmentalUi.years"), accessor: "years" },
        { Header: t("environmentalUi.cumulativePower"), accessor: "cumulativePower" },
        {
          Header: t("environmentalUi.impactPerUnit"),
          accessor: "environmentalImpact",
        },
        { Header: t("environmentalUi.totalImpact"), accessor: "total" },
      ],
      rows: [
        {
          years: 10,
          cumulativePower: response.cumulative_power[0].toFixed(3),
          environmentalImpact: response.environmental_impacts[0].toFixed(3),
          total: (response.cumulative_power[0] * response.environmental_impacts[0]).toFixed(3),
        },
        {
          years: 15,
          cumulativePower: response.cumulative_power[1].toFixed(3),
          environmentalImpact: response.environmental_impacts[1].toFixed(3),
          total: (response.cumulative_power[1] * response.environmental_impacts[1]).toFixed(3),
        },
        {
          years: 30,
          cumulativePower: response.cumulative_power[2].toFixed(3),
          environmentalImpact: response.environmental_impacts[2].toFixed(3),
          total: (response.cumulative_power[2] * response.environmental_impacts[2]).toFixed(3),
        },
        {
          years: serviceLifeYears,
          cumulativePower: response.cumulative_power[3].toFixed(3),
          environmentalImpact: response.environmental_impacts[3].toFixed(3),
          total: (response.cumulative_power[3] * response.environmental_impacts[3]).toFixed(3),
        },
      ],
    });
  }

  async function doSiteChanged(event) {
    handleLoadingOpen();
    try {
      sites.forEach((site) => {
        if (site.id === event.target.value) {
          setSelectedSiteBody(site);
        }
      });
      await fetchSiteImpact(event.target.value);
      await fetchSiteData(event.target.value);
    } finally {
      handleLoadingClose();
    }
  }

  function selectChange(event) {
    setSelectedSite(event.target.value);
    doSiteChanged(event);
  }

  async function fetchSites() {
    const response = await apiClient.get("sites");
    setSites(response);
    if (location.state && location.state.prop) {
      setSelectedSite(location.state.prop.site);
      fetchSiteImpact(location.state.prop.site);
      fetchSiteData(location.state.prop.site);
    }
  }

  useEffect(() => {
    fetchSites();
  }, [i18n.resolvedLanguage]);

  const manufacturingPerPanelImpact = 61.1;
  const recyclingPerPanelImpact = 0.295;
  const impactDiff = 278;
  const impactCategories = [
    { key: "climateChange", manufacturing: 4.31e2, recycling: 3.59, unit: "kg CO₂ eq" },
    { key: "ozoneDepletion", manufacturing: 2.82e-5, recycling: 5.52e-7, unit: "kg CFC-11 eq" },
    { key: "ionizingRadiation", manufacturing: 1.64e1, recycling: 2.05e-1, unit: "kBq U-235 eq" },
    { key: "photochemicalOzone", manufacturing: 2.55, recycling: 5.82e-3, unit: "kg NMVOC eq" },
    {
      key: "respiratoryInorganics",
      manufacturing: 2.62e-5,
      recycling: 6.9e-8,
      unit: t("environmentalValue.impactUnits.diseaseIncidence"),
    },
    { key: "nonCancerEffects", manufacturing: 1.27e-5, recycling: 2.15e-8, unit: "CTUh" },
    { key: "cancerEffects", manufacturing: 2.53e-7, recycling: 9.25e-10, unit: "CTUh" },
    { key: "acidification", manufacturing: 3.57, recycling: 7.84e-3, unit: "mol H+ eq" },
    {
      key: "eutrophicationFreshwater",
      manufacturing: 2.01e-2,
      recycling: 4.52e-5,
      unit: "kg P eq",
    },
    { key: "eutrophicationMarine", manufacturing: 7.93e-1, recycling: 1.68e-3, unit: "kg N eq" },
    { key: "eutrophicationTerrestrial", manufacturing: 8.63, recycling: 1.86e-2, unit: "mol N eq" },
    { key: "ecotoxicityFreshwater", manufacturing: 1.36e4, recycling: 3.08e1, unit: "CTUe" },
    { key: "landUse", manufacturing: 2.11e3, recycling: 1.13e1, unit: "Pt" },
    {
      key: "waterScarcity",
      manufacturing: 1.48e2,
      recycling: 8.08,
      unit: t("environmentalValue.impactUnits.waterScarcity"),
    },
    { key: "resourceUseEnergy", manufacturing: 4.99e3, recycling: 4.0e1, unit: "MJ" },
    { key: "resourceUseMinerals", manufacturing: 1.7e-2, recycling: 3.08e-5, unit: "kg Sb eq" },
  ];
  const renderImpactTable = (impactType, perPanelImpact) => ({
    columns: [
      { Header: t("environmentalUi.impactCategory"), accessor: "category" },
      { Header: t("environmentalUi.equivalentImpact"), accessor: "value" },
      { Header: t("environmentalUi.unit"), accessor: "unit" },
    ],
    rows: impactCategories.map((category) => ({
      category: t(`environmentalValue.impactCategories.${category.key}`),
      value: (
        (category[impactType] / perPanelImpact) *
        impactDiff *
        (selectedSiteBody.panel_number ?? 0)
      ).toExponential(3),
      unit: category.unit,
    })),
  });
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loadingOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox pb={3}>
        <br />
        <MDBox p={5}>
          <FormControl fullWidth>
            <InputLabel id="sites-label">{t("serviceLifePage.sites")}</InputLabel>
            <Select
              labelId="sites-label"
              id="sites-select"
              value={selectedSite}
              label={t("serviceLifePage.selectSite")}
              sx={{ minHeight: "50px", maxWidth: "200px" }}
              onChange={selectChange}
            >
              {sites.map((site, index) => (
                <MenuItem key={index} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%", position: "relative" }}>
            <HelpTooltip
              title={tHelp("environmental.totalImpact")}
              sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
            />
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  assignment
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("environmentalUi.totalImpactPerUnit")}
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <DataTable table={tableData} canSearch />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
          <br />
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <MultiAxisChart
                  icon={{ component: "show_chart" }}
                  title={t("environmentalValue.panelServiceLifeAndOutput")}
                  description="&nbsp;"
                  chart={impactAndCumulativePowerGraphData}
                  helpText={tHelp("environmental.serviceLifeAndOutput")}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <ReportsBarChart
                  icon={{ component: "show_chart" }}
                  title={t("environmentalValue.panelServiceLife")}
                  description="&nbsp;"
                  chart={impactGraphData}
                  helpText={tHelp("environmental.panelServiceLife")}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item lg={12}>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("environmentalUi.operationsImpact")}
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Grid container spacing={3} sx={{ justifyContent: "center" }}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="build"
                  title={
                    <>
                      {t("environmentalUi.manufacturingPanel")}
                      <br />
                      {t("environmentalUi.perPanel")}
                    </>
                  }
                  description=""
                  value={"4.55"}
                  helpText={tHelp("environmental.manufacturingDifference")}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%" }}>
            <MDBox p={2}>
              <DataTable
                table={renderImpactTable("manufacturing", manufacturingPerPanelImpact)}
                entriesPerPage={false}
                showTotalEntries={false}
              />
            </MDBox>
          </Card>
        </MDBox>
        <MDBox mt={5}>
          <br />
          <Grid container spacing={3}>
            <Grid item lg={12}>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("environmentalUi.recyclingImpact")}
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Grid container spacing={3} sx={{ justifyContent: "center" }}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="recycling"
                  title={
                    <>
                      {t("environmentalUi.recyclingPanel")}
                      <br />
                      {t("environmentalUi.perPanel")}
                    </>
                  }
                  description=""
                  value={"440.68"}
                  helpText={tHelp("environmental.recyclingDifference")}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%" }}>
            <MDBox p={2}>
              <DataTable
                table={renderImpactTable("recycling", recyclingPerPanelImpact)}
                entriesPerPage={false}
                showTotalEntries={false}
              />
            </MDBox>
          </Card>
        </MDBox>
        <MDBox mt={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={4}>
              <Card sx={{ position: "relative" }}>
                <HelpTooltip
                  title={tHelp("environmental.remainingServiceLife")}
                  sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
                />
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    {t("environmentalUi.remainingServiceLife")}
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {remainingServiceLife}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Card sx={{ position: "relative" }}>
                <HelpTooltip
                  title={tHelp("environmental.remainingPower")}
                  sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
                />
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    {t("environmentalUi.remainingPower")}
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {(remainingPower * 100).toFixed(1)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Card sx={{ position: "relative" }}>
                <HelpTooltip
                  title={tHelp("environmental.currentWtp")}
                  sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
                />
                <MDBox p={3}>
                  <MDTypography variant="body2" color="text">
                    {t("environmentalUi.currentWtp")}
                  </MDTypography>
                  <MDBox mt={2} mb={1} lineHeight={0}>
                    <MDTypography variant="h3" fontWeight="bold">
                      {currentWTP.toFixed(3)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <WTPChart
                  icon={{ component: "show_chart" }}
                  title={t("environmentalValue.lcoe")}
                  description={t("environmentalValue.lcoeDescription")}
                  chart={lcoeData}
                  helpText={tHelp("environmental.wtpEvolution")}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EnvironmentalValue;
